import { fg } from '@atlaskit/platform-feature-flags';

import type {
	SelectionType,
	UpdateIdMap,
	EditorPluginAIConfigItem,
} from '../config-items/config-items';
import { sliceOrNodeToMarkdown } from '../config-items/slice-or-node-to-markdown';
import { convoAIStatelessApiURL } from '../prebuilt/utils';
import type { IntentSchemaId } from '../provider/prompt-requests/types';
import { getSupportedMarkdowns } from '../provider/prompt-requests/utils/get-supported-markdown-from-schema';
import { type MentionMap } from '../utils/mentions/get-mention-map';

import type { AIExperienceMachineContextForRequest, RequestOptions } from './types';

type Options = {
	context: AIExperienceMachineContextForRequest;
	configItem: EditorPluginAIConfigItem;
	mentionMap?: MentionMap;
	// remove this when cleanup platform_editor_ai_remove_selection_type
	isEmpty?: boolean;
	// remove this when cleanup platform_editor_ai_remove_selection_type
	selectionType?: SelectionType;
	intentSchemaId: IntentSchemaId;
	updateIdMap: UpdateIdMap;
};

// Ignored via go/ees005
// eslint-disable-next-line require-unicode-regexp
const sanitizeUrl = (url: string) => url.replace(/[^\w\-/.:%?=]+/g, '');

export const getRequestOptions4ConvoAI = ({
	context,
	configItem,
	mentionMap,
	isEmpty,
	selectionType,
	intentSchemaId,
	updateIdMap,
}: Options): RequestOptions => {
	const { aiProvider, editorView, positions, intl, latestPromptResponse, userInput } = context;
	const { product } = aiProvider;
	const { experienceId, endpoint, agentId, lang } = configItem;
	const editorSchema = editorView.state.schema;
	const aiSessionId = context.analyticsContext?.aiSessionId;
	const idMap = {};

	// if there is no selection, we want to pass only the doc
	// if there is selection, we want to pass only the selection (if we pass doc and selection, the doc will be ignored by BE)
	let needDoc = fg('platform_editor_ai_remove_selection_type') ? context.isEmpty : isEmpty;
	let needSelection = !(fg('platform_editor_ai_remove_selection_type') ? context.isEmpty : isEmpty);

	if (configItem.alwaysNoDocAndSelection) {
		// for Brainstorm, Jira Draft reply, don't send document (we have history feedback that LLM was using the document too much)
		needDoc = false;
		needSelection = false;
	} else if (configItem.alwaysNeedDoc) {
		// for Jira Improve description
		needDoc = true;
		needSelection = false;
	} else if (
		// for Free generate and interrogation
		configItem.canTogglePageKnowledge
	) {
		// For range selection and usePageKnowledge, we're sending both doc and selection
		needDoc = context.usePageKnowledge;
	}

	const doc = needDoc
		? sliceOrNodeToMarkdown({
				node: editorView.state.doc,
				editorView,
				convertTo: 'markdown-plus',
				existingIdMap: idMap,
				updateIdMap,
				selectionType: fg('platform_editor_ai_remove_selection_type') ? undefined : selectionType,
				mentionMap,
			})
		: undefined;

	const selection = needSelection
		? sliceOrNodeToMarkdown({
				slice: editorView.state.doc.slice(positions[0], positions[1]),
				editorView,
				convertTo: 'markdown-plus',
				existingIdMap: doc?.idMap,
				updateIdMap,
				selectionType: fg('platform_editor_ai_remove_selection_type') ? undefined : selectionType,
				mentionMap,
			})
		: undefined;

	// for Jira Improve description
	const payload = configItem.getRequestPayload?.({
		aiSessionId,
		locale: intl.locale,
		isEmpty: fg('platform_editor_ai_remove_selection_type') ? context.isEmpty : isEmpty,
		doc: doc?.markdown,
		selection: selection?.markdown,
	});

	return {
		doc: doc?.markdown,
		selection: selection?.markdown,
		contentStatistics: doc?.contentStatistics || selection?.contentStatistics,
		isConvoAI: true,
		experienceId: experienceId || 'editor',
		endpoint: endpoint || convoAIStatelessApiURL,
		payload: JSON.stringify(
			payload || {
				recipient_agent_named_id: agentId || 'editor_intent_agent',
				// userInput for Free generate, Free generate interrogation, Brainstorm
				agent_input_prompt: userInput || undefined,
				agent_input_context: {
					ai_session_id: aiSessionId,
					product,
					locale: intl.locale,
					content_url: sanitizeUrl(window.document.location.href),
					input_mime_type: 'text/markdown',
					output_mime_type: 'text/markdown',
					supported_markdown_nodes: getSupportedMarkdowns(editorSchema),
					writing_intent: intentSchemaId,
					draft_full_content: doc?.markdown || undefined,
					// latestPromptResponse for Free generate interrogation, Refine response
					draft_selected_content: latestPromptResponse || selection?.markdown || undefined,
					// for translate
					transformation_subtype: lang,
				},
			},
		),
	};
};
